import { ApiDelete, ApiGet, ApiPost, ApiPut } from "./axiosFunctions";
import { getStoredTokens } from "./authSession";

function finiteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function normalizeComplexity(value, fallback = 3) {
  const number = finiteNumber(value);
  return number === null ? fallback : number;
}

/** Next `sortOrder` so a newly linked card is appended after the current sequence. */
export function nextDeckSortOrder(links = []) {
  const values = (links || [])
    .map((link) => finiteNumber(link?.sortOrder))
    .filter((value) => value !== null);
  if (!values.length) return 0;
  return Math.max(...values) + 1;
}

const localOrderKey = (deckId) => `deck_card_order_${deckId}`;

export function readLocalCardOrder(deckId) {
  if (!deckId) return null;
  try {
    const parsed = JSON.parse(localStorage.getItem(localOrderKey(deckId)) || "null");
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeLocalCardOrder(deckId, cardIds) {
  if (!deckId) return;
  try {
    localStorage.setItem(localOrderKey(deckId), JSON.stringify(cardIds));
  } catch {
    // storage may be unavailable
  }
}

/** True when sortOrder can actually distinguish the cards. */
export function sortOrdersAreDistinct(links = []) {
  const values = (links || [])
    .map((link) => finiteNumber(link?.sortOrder))
    .filter((value) => value !== null);
  return values.length > 1 && new Set(values).size === values.length;
}

function sortByIdList(cards, ids) {
  const rank = new Map(ids.map((id, index) => [id, index]));
  return cards
    .map((card, index) => ({ card, index }))
    .sort((a, b) => {
      const aRank = rank.has(a.card?.cardId) ? rank.get(a.card.cardId) : Number.MAX_SAFE_INTEGER;
      const bRank = rank.has(b.card?.cardId) ? rank.get(b.card.cardId) : Number.MAX_SAFE_INTEGER;
      if (aRank !== bRank) return aRank - bRank;
      return a.index - b.index;
    })
    .map(({ card }) => card);
}

/**
 * Order full card records by the deck document's `cards[].sortOrder`.
 * When every card still shares the same sortOrder, `preferredIds` is used.
 */
export function sortCardsByDeckOrder(cards = [], links = [], preferredIds = null) {
  if (!sortOrdersAreDistinct(links) && preferredIds?.length) {
    return sortByIdList(cards, preferredIds);
  }

  const rank = new Map();
  (links || []).forEach((link, index) => {
    if (!link?.cardId || rank.has(link.cardId)) return;
    const sortOrder = finiteNumber(link.sortOrder);
    rank.set(link.cardId, {
      sortOrder: sortOrder === null ? index : sortOrder,
      index,
    });
  });

  return cards
    .map((card, index) => ({ card, index }))
    .sort((a, b) => {
      const aRank = rank.get(a.card?.cardId);
      const bRank = rank.get(b.card?.cardId);
      const aSort = aRank ? aRank.sortOrder : Number.MAX_SAFE_INTEGER;
      const bSort = bRank ? bRank.sortOrder : Number.MAX_SAFE_INTEGER;
      if (aSort !== bSort) return aSort - bSort;
      const aIndex = aRank ? aRank.index : a.index;
      const bIndex = bRank ? bRank.index : b.index;
      return aIndex - bIndex;
    })
    .map(({ card }) => card);
}

export function buildCardLinks(cards = [], linkMeta = []) {
  const byId = new Map((linkMeta || []).map((link) => [link.cardId, link]));
  return cards.map((card, index) => {
    const meta = byId.get(card.cardId);
    const sortOrder = finiteNumber(meta?.sortOrder);
    return {
      cardId: card.cardId,
      complexity: normalizeComplexity(meta?.complexity ?? card?.complexity),
      sortOrder: sortOrder === null ? index : sortOrder,
    };
  });
}

const SORT_GAP = 1024;

function linkSnapshot(link, sortOrder) {
  return {
    cardId: link.cardId,
    complexity: normalizeComplexity(link.complexity),
    sortOrder,
  };
}

/**
 * Move one card. Only that card's `sortOrder` changes when the neighbors
 * leave a gap. The whole list is spaced out only when two neighbors are
 * too close to fit another integer between them.
 */
export function reorderCardLinks(links, fromIndex, toIndex) {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= links.length ||
    toIndex >= links.length
  ) {
    return links;
  }

  const next = links.slice();
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);

  const before = toIndex > 0 ? finiteNumber(next[toIndex - 1].sortOrder) : null;
  const after = toIndex < next.length - 1 ? finiteNumber(next[toIndex + 1].sortOrder) : null;

  let sortOrder = null;
  if (before === null && after === null) sortOrder = 0;
  else if (before === null) sortOrder = after - SORT_GAP;
  else if (after === null) sortOrder = before + SORT_GAP;
  else if (after - before > 1) sortOrder = before + Math.floor((after - before) / 2);

  const collides =
    sortOrder !== null &&
    next.some((link, index) => index !== toIndex && finiteNumber(link.sortOrder) === sortOrder);

  if (sortOrder === null || collides) {
    return next.map((link, index) => linkSnapshot(link, index * SORT_GAP));
  }

  return next.map((link, index) =>
    index === toIndex ? linkSnapshot(link, sortOrder) : linkSnapshot(link, finiteNumber(link.sortOrder))
  );
}

function changedLinks(previousLinks, nextLinks) {
  const previousById = new Map(previousLinks.map((link) => [link.cardId, link]));
  return nextLinks.filter((link) => {
    const previous = previousById.get(link.cardId);
    return finiteNumber(previous?.sortOrder) !== finiteNumber(link.sortOrder);
  });
}

async function replaceCardLink(deckId, previous, link) {
  const complexity = normalizeComplexity(link.complexity ?? previous?.complexity);
  const body = {
    deckId,
    card: {
      cardId: link.cardId,
      complexity,
      sortOrder: link.sortOrder,
    },
  };

  await ApiDelete(`/contents/decks/${deckId}/cards/${link.cardId}`);
  try {
    await ApiPost("/contents/decks/cards", body);
  } catch (error) {
    if (previous) {
      await ApiPost("/contents/decks/cards", {
        deckId,
        card: {
          cardId: previous.cardId,
          complexity: normalizeComplexity(previous.complexity, complexity),
          sortOrder: finiteNumber(previous.sortOrder) ?? 0,
        },
      }).catch(() => {});
    }
    throw error;
  }
}

/** One deck update when several `sortOrder` values change together. */
async function saveCardOrderOnDeck(deckId, nextLinks) {
  const response = await ApiGet(`/contents/decks/${deckId}`);
  const deck = response?.data;
  if (!deck?.deckId) {
    const error = new Error("Deck not found");
    throw error;
  }

  const nextById = new Map(nextLinks.map((link) => [link.cardId, link]));
  const cards = (deck.cards || []).map((card) => {
    const next = nextById.get(card.cardId);
    return {
      cardId: card.cardId,
      complexity: next ? next.complexity : card.complexity,
      sortOrder: next ? next.sortOrder : card.sortOrder,
    };
  });

  await ApiPut("/contents/decks", {
    _id: deck._id || deck.deckId,
    deckId: deck.deckId,
    authorId: deck.authorId,
    status: deck.status,
    deckName: deck.deckName,
    shortDescription: deck.shortDescription,
    longDescription: deck.longDescription,
    mainSubject: deck.mainSubject,
    smallImageUrl: deck.smallImageUrl,
    largeImageUrl: deck.largeImageUrl,
    language: deck.language,
    countries: deck.countries,
    schoolGrades: deck.schoolGrades,
    folder: deck.folder,
    cards,
  });
}

/**
 * Persist order on the deck document's `cards[].sortOrder`.
 * One moved card is two calls. A full renumber is a single deck update.
 */
export async function persistDeckCardSortOrder(deckId, previousLinks, nextLinks) {
  if (!getStoredTokens()?.accessToken) {
    const error = new Error("Login required to change card order");
    error.code = "LOGIN_REQUIRED";
    throw error;
  }

  const previousById = new Map(previousLinks.map((link) => [link.cardId, link]));
  const changed = changedLinks(previousLinks, nextLinks);
  if (!changed.length) return;

  if (changed.length > 1) {
    try {
      await saveCardOrderOnDeck(deckId, nextLinks);
      return;
    } catch {
      // The deck update did not apply `cards`. Write the changed links one by one.
    }
  }

  for (const link of changed) {
    await replaceCardLink(deckId, previousById.get(link.cardId), link);
  }
}
